import React, { FC, memo, useCallback, useState } from 'react'
import { HStack, Grid } from '@chakra-ui/react'
import { PhoneSpecs } from '../../phones'
import { PhoneListProps } from './phone-list'
import { IconButton } from '../icon-button'
import { CustomSortIcon } from '../icon'
import { Dropdown } from '../dropdown'
import i18next from 'i18next'
import { SortOption } from '../../state/interfaces'

export interface SortedPhoneListProps extends PhoneListProps {
  PhoneList: React.ComponentType<PhoneListProps>
}

const getSortOptionValue = (value: string) => Object.entries(SortOption).find(([key]) => key === value)?.[1]

const sortOptions = Object.entries(SortOption).reduce((accumulator, [value, label]) => {
  label = i18next.t(label)
  return [...accumulator, { label, value }]
}, [] as Array<{ label: string; value: string }>)

export const SortedPhoneList: FC<SortedPhoneListProps> = memo(({ PhoneList, phones, onClick }) => {
  const [sortBy, setSortBy] = useState<SortOption>(i18next.t(SortOption.POPULARITY))
  const [sortInAscendingOrder, setSortInAscendingOrder] = useState(true)

  const toggleSortOrder = useCallback(() => {
    setSortInAscendingOrder(!sortInAscendingOrder)
  }, [sortInAscendingOrder])

  const changeSortBy = (sortOption: SortOption) => {
    setSortBy(sortOption)
  }

  const sortPhones = useCallback(
    (phones: PhoneSpecs[]) => {
      const sortedPhones = [...phones].sort((a, b) => {
        switch (getSortOptionValue(sortBy)) {
          case SortOption.PHONE_PRICE:
            return b.modelPrice - a.modelPrice
          case SortOption.PHONE_NAME:
            return b.name.localeCompare(a.name)
          case SortOption.MEMORY_SIZE:
            return b.modelMemorySize - a.modelMemorySize
          default:
          case SortOption.POPULARITY:
            if (b.position === a.position) {
              return b.modelMemorySize - a.modelMemorySize
            }
            return b.position - a.position
        }
      })

      if (sortInAscendingOrder) {
        sortedPhones.reverse()
      }

      return sortedPhones
    },
    [sortBy, sortInAscendingOrder],
  )

  return (
    <>
      {phones.length > 0 && (
        <Grid templateColumns="repeat(auto-fill, 298px)" width="100%" justifyContent="center" marginTop={4}>
          <HStack width="full" paddingLeft={2}>
            <HStack
              height={50}
              border={2}
              paddingLeft={2}
              paddingRight={2}
              borderStyle="solid"
              borderColor="grey"
              borderRadius="medium"
              justifyContent="center"
              backgroundColor="white"
              _hover={{
                borderColor: 'brand',
                boxShadow: '0 0 0 2px #5bce4c',
              }}
            >
              <IconButton
                label="sort"
                size="sm"
                icon={<CustomSortIcon color="green.500" height="16px" />}
                onClick={toggleSortOrder}
              />
            </HStack>

            <Dropdown
              options={sortOptions}
              value={sortBy}
              onChange={(value) => changeSortBy(value as SortOption)}
              excludeSelected={true}
            />
          </HStack>
        </Grid>
      )}
      <PhoneList phones={sortPhones(phones)} onClick={onClick} />
    </>
  )
})
